import React, { useMemo } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Descriptions,
    Divider,
    Empty,
    Row,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import {
    AlertOutlined,
    AppstoreOutlined,
    ArrowRightOutlined,
    BarChartOutlined,
    BulbOutlined,
    ExperimentOutlined,
    RadarChartOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useGetSystemSettingsByCategoryQuery } from '../../../services/nestjs-base-api';

const { Text } = Typography;

interface FeatureSpec {
    title: string;
    icon: React.ReactNode;
    description: string;
    /** AI Hub subsystem id — used for the "Change" button URL. */
    subsystemId: string;
    /** Optional explicit deep-link path. Defaults to `/admin/ai/${subsystemId}`. */
    path?: string;
    primaryProviderKey: string;
    primaryModelKey: string;
    primaryTemperatureKey: string;
    fallbackProviderKey: string;
    fallbackModelKey: string;
    fallbackTemperatureKey: string;
}

const FEATURES: FeatureSpec[] = [
    {
        title: 'AI Expert System',
        icon: <ExperimentOutlined />,
        description:
            'Conversational AI experts (Brand Professor, Head of Brand Marketing, Data Analyst).',
        subsystemId: 'ai_experts',
        // AI_EXPERT_* defaults live on the Platform defaults inner tab.
        path: '/admin/ai/ai_experts?section=defaults',
        primaryProviderKey: 'AI_EXPERT_PRIMARY_PROVIDER',
        primaryModelKey: 'AI_EXPERT_PRIMARY_MODEL',
        primaryTemperatureKey: 'AI_EXPERT_PRIMARY_TEMPERATURE',
        fallbackProviderKey: 'AI_EXPERT_FALLBACK_PROVIDER',
        fallbackModelKey: 'AI_EXPERT_FALLBACK_MODEL',
        fallbackTemperatureKey: 'AI_EXPERT_FALLBACK_TEMPERATURE',
    },
    {
        title: 'Dashboard AI',
        icon: <BarChartOutlined />,
        description:
            'AI that generates per-expert dashboard extracts. Lives inside the AI Experts subsystem.',
        subsystemId: 'ai_experts',
        // DASHBOARD_AI_* defaults are also on the Platform defaults inner tab.
        path: '/admin/ai/ai_experts?section=defaults',
        primaryProviderKey: 'DASHBOARD_AI_PRIMARY_PROVIDER',
        primaryModelKey: 'DASHBOARD_AI_PRIMARY_MODEL',
        primaryTemperatureKey: 'DASHBOARD_AI_PRIMARY_TEMPERATURE',
        fallbackProviderKey: 'DASHBOARD_AI_FALLBACK_PROVIDER',
        fallbackModelKey: 'DASHBOARD_AI_FALLBACK_MODEL',
        fallbackTemperatureKey: 'DASHBOARD_AI_FALLBACK_TEMPERATURE',
    },
    {
        title: 'Advice AI',
        icon: <BulbOutlined />,
        description:
            'Five-section structured brand advice (key findings, explore further, opportunities, threats, notable changes).',
        subsystemId: 'advice_ai',
        primaryProviderKey: 'ADVICE_AI_PRIMARY_PROVIDER',
        primaryModelKey: 'ADVICE_AI_PRIMARY_MODEL',
        primaryTemperatureKey: 'ADVICE_AI_PRIMARY_TEMPERATURE',
        fallbackProviderKey: 'ADVICE_AI_FALLBACK_PROVIDER',
        fallbackModelKey: 'ADVICE_AI_FALLBACK_MODEL',
        fallbackTemperatureKey: 'ADVICE_AI_FALLBACK_TEMPERATURE',
    },
    {
        title: 'Orbital AI',
        icon: <RadarChartOutlined />,
        description:
            'Synthesises Advice AI output into the dashboard orbital narrative (findings + actions).',
        subsystemId: 'orbital_ai',
        primaryProviderKey: 'ORBITAL_AI_PRIMARY_PROVIDER',
        primaryModelKey: 'ORBITAL_AI_PRIMARY_MODEL',
        primaryTemperatureKey: 'ORBITAL_AI_PRIMARY_TEMPERATURE',
        fallbackProviderKey: 'ORBITAL_AI_FALLBACK_PROVIDER',
        fallbackModelKey: 'ORBITAL_AI_FALLBACK_MODEL',
        fallbackTemperatureKey: 'ORBITAL_AI_FALLBACK_TEMPERATURE',
    },
    {
        title: 'Building Blocks AI',
        icon: <AppstoreOutlined />,
        description:
            'Per-element building-block summaries on the dashboard (combined or individual mode).',
        subsystemId: 'building_blocks_ai',
        primaryProviderKey: 'BUILDING_BLOCKS_AI_PRIMARY_PROVIDER',
        primaryModelKey: 'BUILDING_BLOCKS_AI_PRIMARY_MODEL',
        primaryTemperatureKey: 'BUILDING_BLOCKS_AI_PRIMARY_TEMPERATURE',
        fallbackProviderKey: 'BUILDING_BLOCKS_AI_FALLBACK_PROVIDER',
        fallbackModelKey: 'BUILDING_BLOCKS_AI_FALLBACK_MODEL',
        fallbackTemperatureKey: 'BUILDING_BLOCKS_AI_FALLBACK_TEMPERATURE',
    },
    {
        title: 'Element Analysis',
        icon: <ThunderboltOutlined />,
        description:
            'Measured + latent element first-thoughts. Only allow-listed consumer of the confidential ELEMENT_WEIGHTINGS_MD placeholder.',
        subsystemId: 'element_analysis',
        primaryProviderKey: 'AI_ELEMENT_ANALYSIS_PROVIDER',
        primaryModelKey: 'AI_ELEMENT_ANALYSIS_MODEL',
        primaryTemperatureKey: 'AI_ELEMENT_ANALYSIS_TEMPERATURE',
        fallbackProviderKey: 'AI_ELEMENT_ANALYSIS_FALLBACK_PROVIDER',
        fallbackModelKey: 'AI_ELEMENT_ANALYSIS_FALLBACK_MODEL',
        fallbackTemperatureKey: 'AI_ELEMENT_ANALYSIS_FALLBACK_TEMPERATURE',
    },
    {
        title: 'Response Monitoring AI',
        icon: <AlertOutlined />,
        description:
            'Scheduled review of survey response volumes per brand; LLM authors the alert message.',
        subsystemId: 'response_monitoring_ai',
        // Provider/model live on the LLM inner tab.
        path: '/admin/ai/response_monitoring_ai?section=llm',
        primaryProviderKey: 'RESPONSE_MONITORING_AI_PROVIDER',
        primaryModelKey: 'RESPONSE_MONITORING_AI_MODEL',
        primaryTemperatureKey: 'RESPONSE_MONITORING_AI_PRIMARY_TEMPERATURE',
        fallbackProviderKey: 'RESPONSE_MONITORING_AI_FALLBACK_PROVIDER',
        fallbackModelKey: 'RESPONSE_MONITORING_AI_FALLBACK_MODEL',
        fallbackTemperatureKey: 'RESPONSE_MONITORING_AI_FALLBACK_TEMPERATURE',
    },
];

/**
 * Read-only summary of every AI subsystem's provider/model/temperature.
 *
 * After the SYR-33 cutovers each subsystem has a dedicated Configure page in
 * the AI Hub with versioning, draft/publish, and confidentiality controls.
 * This tab keeps the at-a-glance scan that admins like, but the "Change"
 * buttons send them to the canonical hub editor — there's only one writeable
 * surface for these settings now.
 */
export const AISystemTab: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const { data: categorizedData, isLoading } = useGetSystemSettingsByCategoryQuery({
        jwtToken: user?.jwtToken || '',
    });

    const settingsMap = useMemo(() => {
        const map = new Map<string, string | undefined>();
        if (categorizedData?.categories) {
            for (const category of categorizedData.categories) {
                for (const setting of category.settings) {
                    map.set(setting.name, setting.value);
                }
            }
        }
        return map;
    }, [categorizedData]);

    const get = (key: string): string | undefined => {
        const v = settingsMap.get(key);
        return v === undefined || v === null || v === '' ? undefined : v;
    };

    if (isLoading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (settingsMap.size === 0) {
        return <Empty description="Could not load system settings." />;
    }

    const goToHub = (path: string) => navigate(path);

    return (
        <div>
            <Alert
                message="AI System — read-only summary"
                description="At-a-glance view of every AI subsystem's provider/model/temperature. Editing is now centralised in the AI Hub (/admin/ai), where each subsystem has versioning, draft/publish, and a sandbox. Use the Change button on each card to jump straight to the canonical editor."
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
            />

            {FEATURES.map((feature) => {
                const path = feature.path ?? `/admin/ai/${feature.subsystemId}`;
                return (
                    <FeatureSummary
                        key={feature.title}
                        feature={feature}
                        get={get}
                        onChange={() => goToHub(path)}
                    />
                );
            })}

            <Divider />

            <Card
                title={
                    <Space>
                        <ExperimentOutlined />
                        <span>AI Expert embedding model</span>
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => goToHub('/admin/ai/ai_experts?section=defaults')}
                    >
                        Change <ArrowRightOutlined />
                    </Button>
                }
                styles={{ body: { paddingTop: 12 } }}
            >
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Embedding model used by the AI Experts knowledge base for vector
                    representations.
                </Text>
                <Descriptions size="small" column={1} bordered>
                    <Descriptions.Item label="AI_EXPERT_EMBEDDING_MODEL">
                        <ValueOrMissing value={get('AI_EXPERT_EMBEDDING_MODEL')} />
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        </div>
    );
};

interface FeatureSummaryProps {
    feature: FeatureSpec;
    get: (key: string) => string | undefined;
    onChange: () => void;
}

const FeatureSummary: React.FC<FeatureSummaryProps> = ({ feature, get, onChange }) => {
    return (
        <Card
            title={
                <Space>
                    {feature.icon}
                    <span>{feature.title}</span>
                </Space>
            }
            extra={
                <Button type="primary" size="small" onClick={onChange}>
                    Change <ArrowRightOutlined />
                </Button>
            }
            style={{ marginBottom: 16 }}
            styles={{ body: { paddingTop: 12 } }}
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                {feature.description}
            </Text>
            <Row gutter={16}>
                <Col xs={24} lg={12}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                        Primary
                    </Text>
                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Provider">
                            <ValueOrMissing value={get(feature.primaryProviderKey)} mono />
                        </Descriptions.Item>
                        <Descriptions.Item label="Model">
                            <ValueOrMissing value={get(feature.primaryModelKey)} mono />
                        </Descriptions.Item>
                        <Descriptions.Item label="Temperature">
                            <ValueOrMissing value={get(feature.primaryTemperatureKey)} mono />
                        </Descriptions.Item>
                    </Descriptions>
                </Col>
                <Col xs={24} lg={12}>
                    <Text strong style={{ display: 'block', marginBottom: 6 }}>
                        Fallback{' '}
                        <Tag color="default" style={{ fontWeight: 400 }}>
                            used only if primary fails
                        </Tag>
                    </Text>
                    <Descriptions size="small" column={1} bordered>
                        <Descriptions.Item label="Provider">
                            <ValueOrMissing value={get(feature.fallbackProviderKey)} mono />
                        </Descriptions.Item>
                        <Descriptions.Item label="Model">
                            <ValueOrMissing value={get(feature.fallbackModelKey)} mono />
                        </Descriptions.Item>
                        <Descriptions.Item label="Temperature">
                            <ValueOrMissing value={get(feature.fallbackTemperatureKey)} mono />
                        </Descriptions.Item>
                    </Descriptions>
                </Col>
            </Row>
        </Card>
    );
};

const ValueOrMissing: React.FC<{ value: string | undefined; mono?: boolean }> = ({
    value,
    mono,
}) => {
    if (value === undefined) {
        return (
            <Text type="secondary" italic>
                (not set — using compiled default)
            </Text>
        );
    }
    return mono ? <code>{value}</code> : <Text>{value}</Text>;
};
